/**
 * Recently Viewed Products Component
 * Loads and displays recently viewed products using Shopify Search API
 * and renders them using the product-card snippet
 */

const STORAGE_KEY = 'theme:recently-viewed-products';

/**
 * Get the locale-aware search URL
 * Extracts locale from current path if window.theme is not available
 */
function getSearchUrl() {
  // Prefer window.theme if available
  if (window.theme?.routes?.search_url) {
    return window.theme.routes.search_url;
  }

  // Extract locale from current path (e.g., /de-de/products/... -> /de-de or /de/products/... -> /de)
  const pathname = window.location.pathname;
  const localeMatch = pathname.match(/^\/([a-z]{2}(?:-[a-z]{2})?)(?:\/|$)/);

  if (localeMatch) {
    return `/${localeMatch[1]}/search`;
  }

  return '/search';
}

class RecentlyViewedProducts extends HTMLElement {
  constructor() {
    super();
    this.loading = false;
    this.products = [];
  }

  connectedCallback() {
    this.limit = parseInt(this.getAttribute('data-limit')) || 8;
    this.displayMode = this.getAttribute('data-display-mode') || 'grid';
    this.sectionId = this.getAttribute('data-section-id');

    // Set up Intersection Observer as fallback
    if ('IntersectionObserver' in window) {
      this.observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting && !this.loading && !this.loaded) {
            this.loadProducts();
            this.observer.disconnect();
          }
        });
      }, {
        rootMargin: '400px'
      });

      this.observer.observe(this);
    }

    // Preemptive loading during idle time
    this.schedulePreemptiveLoad();
  }

  schedulePreemptiveLoad() {
    if (document.readyState === 'complete') {
      this.queuePreemptiveLoad();
    } else {
      window.addEventListener('load', () => this.queuePreemptiveLoad(), { once: true });
    }
  }

  queuePreemptiveLoad() {
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => {
        if (!this.loading && !this.loaded) {
          setTimeout(() => {
            if (!this.loading && !this.loaded) {
              this.loadProducts();
            }
          }, 200); // Slightly later than related products
        }
      }, { timeout: 3500 });
    } else {
      setTimeout(() => {
        if (!this.loading && !this.loaded) {
          this.loadProducts();
        }
      }, 2000);
    }
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  /**
   * Get recently viewed product handles from localStorage
   */
  getRecentlyViewedHandles() {
    try {
      const stored = localStorage.getItem(STORAGE_KEY);
      if (!stored) {
        return [];
      }

      const parsed = JSON.parse(stored);
      return Array.isArray(parsed) ? parsed : [];
    } catch (error) {
      console.error('Error reading recently viewed products:', error);
      return [];
    }
  }

  /**
   * Get the current product handle to exclude from results
   */
  getCurrentProductHandle() {
    const metaTag = document.querySelector('meta[property="og:url"]');
    if (metaTag) {
      const url = metaTag.getAttribute('content');
      const match = url.match(/\/products\/([^/?]+)/);
      if (match) {
        return match[1];
      }
    }

    const pathname = window.location.pathname;
    const match = pathname.match(/\/products\/([^/?]+)/);
    return match ? match[1] : null;
  }

  /**
   * Load product data from Shopify
   */
  async loadProducts() {
    if (this.loading) {
      return;
    }

    this.loading = true;

    try {
      // Get recently viewed handles
      let handles = this.getRecentlyViewedHandles();

      // Filter out current product if on a product page
      const currentHandle = this.getCurrentProductHandle();
      if (currentHandle) {
        handles = handles.filter(h => h !== currentHandle);
      }

      // Check if we have more products than the limit
      const hasMoreProducts = handles.length > this.limit;

      // Limit to the number we want to display
      handles = handles.slice(0, this.limit);

      // If no products to show, hide the section
      if (handles.length === 0) {
        this.hideSection();
        return;
      }

      // Store handles for rendering
      this.productHandles = handles;
      await this.renderProducts();
      this.showSection();

      // Show "View all" button if there are more products
      this.toggleViewAllButton(hasMoreProducts);
    } catch (error) {
      console.error('Error loading recently viewed products:', error);
      this.hideSection();
    } finally {
      this.loading = false;
      this.loaded = true;
    }
  }

  /**
   * Toggle View All button visibility based on product count
   */
  toggleViewAllButton(show) {
    const contentContainer = this.querySelector('.section-resource-list__content');
    if (contentContainer) {
      if (show) {
        contentContainer.classList.add('show-view-all');
      } else {
        contentContainer.classList.remove('show-view-all');
      }
    }
  }

  /**
   * Render products using Shopify search with section rendering
   * This ensures full SSR rendering including mock images and other custom features
   */
  async renderProducts() {
    try {
      // Build search query with product handles
      const searchQuery = this.productHandles.map(handle => `handle:${handle}`).join(' OR ');

      // Fetch rendered section using search endpoint
      const searchUrl = getSearchUrl();
      const url = `${searchUrl}?type=product&q=${encodeURIComponent(searchQuery)}&section_id=${encodeURIComponent(this.sectionId)}`;

      const response = await fetch(url);

      if (!response.ok) {
        throw new Error('Failed to fetch section HTML');
      }

      const html = await response.text();

      // Parse the response to extract the product grid
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Find the product grid in the rendered section
      const productGrid = doc.querySelector('.recently-viewed-products__grid');

      if (productGrid) {
        const targetContainer = this.querySelector('.recently-viewed-products__grid');
        if (targetContainer) {
          // Copy the FULL grid contents from the response — preserves the
          // carousel scaffolding (slideshow + slideshow-slides + slides)
          // when in carousel layout, so per-slide width clamps apply. Then
          // reorder the moveable units (slides if present, else items) to
          // match the localStorage handle order.
          targetContainer.innerHTML = productGrid.innerHTML;
          this.reorderToMatchHandles(targetContainer);
          // After innerHTML swap the slideshow's internal scroll position
          // can land mid-track (browser scroll restoration + scroll-snap
          // can settle on a partially-visible slide). Force the track back
          // to the start so the first card is fully visible on load.
          this.resetCarouselScroll(targetContainer);
          requestAnimationFrame(() => {
            this.reinitializeRivoButtons();
          });
        }
      } else {
        // Fallback: try to find resource-list
        const resourceList = doc.querySelector('.resource-list');
        if (resourceList) {
          const targetContainer = this.querySelector('.recently-viewed-products__grid');
          if (targetContainer) {
            targetContainer.innerHTML = resourceList.innerHTML;
            this.reorderToMatchHandles(targetContainer);
            requestAnimationFrame(() => {
              this.reinitializeRivoButtons();
            });
          }
        } else {
          // If neither found, use fallback rendering
          this.renderProductsFallback();
        }
      }
    } catch (error) {
      console.error('Error rendering products via search:', error);
      // Fallback to basic rendering
      this.renderProductsFallback();
    }
  }

  /**
   * Snap the carousel track back to the start so the first slide is fully
   * visible on load. We do this twice — once synchronously and once after
   * the next frame — because scroll-snap can settle on a partially-
   * visible slide once layout commits after the innerHTML swap.
   * @param {HTMLElement} grid
   */
  resetCarouselScroll(grid) {
    const tracks = grid.querySelectorAll('slideshow-slides, .resource-list__carousel slideshow-slides');
    const snap = () => {
      tracks.forEach((track) => {
        if (typeof track.scrollTo === 'function') {
          track.scrollTo({ left: 0, behavior: 'auto' });
        } else {
          track.scrollLeft = 0;
        }
      });
    };
    snap();
    requestAnimationFrame(snap);
  }

  /**
   * Reorder the moveable units inside the grid to match the productHandles
   * order. Operates on slides when carousel layout is in use (preserves the
   * slide wrapper for slideshow-slides), otherwise on bare items.
   * @param {HTMLElement} grid - The .recently-viewed-products__grid container
   */
  reorderToMatchHandles(grid) {
    const slides = Array.from(grid.querySelectorAll('.resource-list__slide'));
    const items = Array.from(grid.querySelectorAll('.resource-list__item'));
    const movable = slides.length > 0 ? slides : items;
    if (!movable.length) return;

    const handleMap = new Map();
    movable.forEach((node) => {
      const itemEl = node.classList.contains('resource-list__item')
        ? node
        : node.querySelector('.resource-list__item');
      const link = itemEl?.querySelector('a[href*="/products/"]');
      if (!link) return;
      const match = link.getAttribute('href').match(/\/products\/([^/?]+)/);
      if (match) handleMap.set(match[1], node);
    });

    const parent = movable[0].parentNode;
    if (!parent) return;
    this.productHandles.forEach((handle) => {
      const node = handleMap.get(handle);
      if (node) parent.appendChild(node); // re-append in handle order
    });
  }

  /**
   * Legacy: kept for any external caller. Returns sorted item outerHTML
   * (loses carousel slide wrappers — prefer reorderToMatchHandles).
   * @param {HTMLElement} container - The container with product cards
   * @returns {string} Sorted HTML
   */
  sortProductCards(container) {
    const items = Array.from(container.querySelectorAll('.resource-list__item'));
    const cardMap = new Map();
    items.forEach(item => {
      const link = item.querySelector('a[href*="/products/"]');
      if (link) {
        const href = link.getAttribute('href');
        const match = href.match(/\/products\/([^/?]+)/);
        if (match) cardMap.set(match[1], item);
      }
    });
    const sortedItems = [];
    for (const handle of this.productHandles) {
      const item = cardMap.get(handle);
      if (item) sortedItems.push(item);
    }
    return sortedItems.map(item => item.outerHTML).join('');
  }

  /**
   * Fallback rendering method - shows a message since we don't have full product data
   */
  renderProductsFallback() {
    const container = this.querySelector('.recently-viewed-products__grid');
    if (!container) {
      return;
    }

    // Without full product data, we can't render a meaningful fallback
    // Just show a simple message
    container.innerHTML = '<p>Unable to load recently viewed products. Please refresh the page.</p>';
  }

  /**
   * Reinitialize Rivo favorite buttons after dynamically loading content
   */
  reinitializeRivoButtons() {
    try {
      if (window.RivoProfileAlpine) {
        const rivoAccountWidget = document.getElementById('rivo-account-slideout');
        if (rivoAccountWidget) {
          window.RivoProfileAlpine.$data(rivoAccountWidget).initializeFavoriteButtons();
        }
      }
    } catch (error) {
      console.error('Error reinitializing Rivo buttons:', error);
    }
  }

  /**
   * Hide the section
   */
  hideSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = 'none';
    }
  }

  /**
   * Show the section
   */
  showSection() {
    const section = this.closest('.shopify-section');
    if (section) {
      section.style.display = '';
    }
  }
}

// Register the custom element
if (!customElements.get('recently-viewed-products')) {
  customElements.define('recently-viewed-products', RecentlyViewedProducts);
}

/**
 * RecentlyViewed - Utility class for managing recently viewed products
 * Used by predictive search and other components
 *
 * IMPORTANT: This shares the same localStorage key with RecentlyViewedTracker
 * and ONLY stores product HANDLES (strings like "product-handle-slug").
 * The native implementation (RecentlyViewedTracker) also stores handles only.
 */
export class RecentlyViewed {
  static #STORAGE_KEY = 'theme:recently-viewed-products';
  static #MAX_PRODUCTS = 12;

  /**
   * Adds a product to the recently viewed products list.
   * @param {string} productHandle - The handle of the product to add.
   */
  static addProduct(productHandle) {
    // Store product handle (compatible with RecentlyViewedTracker)
    try {
      let viewedProducts = this.getProducts();

      viewedProducts = viewedProducts.filter((handle) => handle !== productHandle);
      viewedProducts.unshift(productHandle);
      viewedProducts = viewedProducts.slice(0, this.#MAX_PRODUCTS);

      localStorage.setItem(this.#STORAGE_KEY, JSON.stringify(viewedProducts));
    } catch (error) {
      console.error('Error adding product to recently viewed:', error);
    }
  }

  /**
   * Clears all recently viewed products from localStorage
   */
  static clearProducts() {
    try {
      localStorage.removeItem(this.#STORAGE_KEY);
    } catch (error) {
      console.error('Error clearing recently viewed products:', error);
    }
  }

  /**
   * Retrieves the list of recently viewed product handles from localStorage.
   * @returns {string[]} The list of viewed product handles.
   */
  static getProducts() {
    try {
      return JSON.parse(localStorage.getItem(this.#STORAGE_KEY) || '[]');
    } catch (error) {
      console.error('Error getting recently viewed products:', error);
      return [];
    }
  }
}
